import { ArrowRight, Play, Sparkles } from 'lucide-react';
import type { HeroSettings } from '../../../types/site';

interface HeroBlockProps {
  settings: HeroSettings;
  primaryColor: string;
  onScrollToForm?: () => void;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : { r: 59, g: 130, b: 246 };
}

function adjustColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const adjust = (c: number) => Math.min(255, Math.max(0, c + amount));
  return `rgb(${adjust(r)}, ${adjust(g)}, ${adjust(b)})`;
}

export function HeroBlock({ settings, primaryColor, onScrollToForm }: HeroBlockProps) {
  const { title, subtitle, imageUrl, buttonLabel, layout } = settings;

  const handleButtonClick = () => {
    if (onScrollToForm) {
      onScrollToForm();
    }
  };

  const lighterColor = adjustColor(primaryColor, 40);

  if (layout === 'center') {
    return (
      <section className="relative py-24 md:py-32 px-4 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}08 0%, ${primaryColor}15 50%, ${lighterColor}10 100%)`,
          }}
        />
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full opacity-20 blur-3xl" style={{ backgroundColor: primaryColor }} />
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ backgroundColor: lighterColor }} />

        <div className="relative max-w-4xl mx-auto text-center">
          {imageUrl && (
            <div className="relative inline-block mb-10">
              <div
                className="absolute inset-0 rounded-full blur-2xl opacity-30"
                style={{ backgroundColor: primaryColor }}
              />
              <img
                src={imageUrl}
                alt=""
                className="relative w-36 h-36 md:w-44 md:h-44 rounded-full object-cover ring-4 ring-white shadow-2xl"
              />
              <div
                className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: primaryColor }}
              >
                <Sparkles className="w-6 h-6 text-white" />
              </div>
            </div>
          )}

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
            {title}
          </h1>

          {subtitle && (
            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}

          {buttonLabel && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleButtonClick}
                className="group relative px-8 py-4 text-white rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center gap-3"
                style={{
                  backgroundColor: primaryColor,
                  boxShadow: `0 10px 40px -10px ${primaryColor}80`,
                }}
              >
                {buttonLabel}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          )}

          <div className="mt-16 flex items-center justify-center gap-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span>Sofort verfügbar</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />
              <span>Kostenlose Beratung</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const isImageLeft = layout === 'image-left';

  return (
    <section className="relative py-20 md:py-28 px-4 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${primaryColor}05 0%, transparent 50%, ${lighterColor}08 100%)`,
        }}
      />
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-10 blur-3xl -translate-y-1/2 translate-x-1/3" style={{ backgroundColor: primaryColor }} />

      <div className="relative max-w-7xl mx-auto">
        <div className={`flex flex-col ${isImageLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 lg:gap-20`}>
          {imageUrl && (
            <div className="flex-1 w-full max-w-lg lg:max-w-none">
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ backgroundColor: primaryColor }}
                />
                <div className="relative">
                  <img
                    src={imageUrl}
                    alt=""
                    className="w-full rounded-3xl shadow-2xl object-cover aspect-[4/5] lg:aspect-[3/4]"
                  />
                  <div
                    className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 shadow-xl hidden md:flex items-center gap-3"
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <Play className="w-6 h-6" style={{ color: primaryColor }} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">100+ zufriedene</p>
                      <p className="text-xs text-gray-500">Kunden vertrauen uns</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={`flex-1 ${!imageUrl ? 'text-center max-w-3xl mx-auto' : ''}`}>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}
            >
              <Sparkles className="w-4 h-4" />
              <span>Willkommen</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
              {title}
            </h1>

            {subtitle && (
              <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed max-w-xl">
                {subtitle}
              </p>
            )}

            {buttonLabel && (
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <button
                  onClick={handleButtonClick}
                  className="group relative px-8 py-4 text-white rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center gap-3"
                  style={{
                    backgroundColor: primaryColor,
                    boxShadow: `0 10px 40px -10px ${primaryColor}80`,
                  }}
                >
                  {buttonLabel}
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            )}

            <div className="mt-12 pt-8 border-t border-gray-100 grid grid-cols-3 gap-6">
              <div>
                <p className="text-3xl font-bold text-gray-900">100+</p>
                <p className="text-sm text-gray-500 mt-1">Zufriedene Kunden</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">5.0</p>
                <p className="text-sm text-gray-500 mt-1">Bewertung</p>
              </div>
              <div>
                <p className="text-3xl font-bold" style={{ color: primaryColor }}>24h</p>
                <p className="text-sm text-gray-500 mt-1">Antwortzeit</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
